import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ibsys/kit';
import { InsightItem, type IndicatorSelectHandler } from '@/entities/dashboards';
import { DASHBOARD_BLOCK_SCROLL_CLASS, cn, preventShiftSelection, zoneCardClass } from '@/shared/lib';
import { EmptyState } from '@/shared/ui';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS } from './utils';

type Props = {
  insights: InsightItem[];
  /** Переход к диалогам, из которых инсайт выведен */
  onSelectInsight: IndicatorSelectHandler;
};

/**
 * Карточка «Авто-инсайты»: список цветных текстовых карточек по тону инсайта
 * (зелёный — позитив, жёлтый — внимание, красный — проблема). Тексты готовы
 * на бэкенде — клиент только раскрашивает их по типу.
 *
 * Клик по инсайту открывает диалоги, из которых он выведен. Инсайт без диалогов
 * разбирать не в чем, поэтому переход по нему не предлагается.
 */
export const InsightsCard: FC<Props> = ({ insights, onSelectInsight }) => {
  const { t } = useTranslation('qualityDashboard');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('insights.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {insights.length === 0 ? (
          <EmptyState />
        ) : (
          <div className={`${DASHBOARD_BLOCK_SCROLL_CLASS} flex flex-col gap-2`}>
            {insights.map((insight, index) => {
              const isClickable = !!insight.selection;
              const className = cn('rounded-lg border p-3 text-sm', zoneCardClass[insight.type]);

              if (!isClickable) {
                return (
                  <div key={index} className={className}>
                    {insight.text}
                  </div>
                );
              }

              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={-1}
                  aria-label={t('insights.openDialogs')}
                  className={cn(className, DASHBOARD_CLICKABLE_BLOCK_CLASS)}
                  onMouseDown={preventShiftSelection}
                  onClick={(event) =>
                    onSelectInsight({ label: insight.text, selection: insight.selection }, event.shiftKey)
                  }
                >
                  {insight.text}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
